<template>
  <div class="saml-form mx-4 col-6">
    <b-form-group class="form-group_center" label="Enable SAML Integration" label-cols="4">
      <b-form-checkbox type="checkbox" v-model="settings.saml_enabled" :disabled="saving" />
    </b-form-group>
    <template v-if="settings.saml_enabled">
      <b-form-group class="form-group_center" label="Enable Login by Password" label-cols="4">
        <b-form-checkbox type="checkbox" v-model="settings.saml_password_login_enabled" :disabled="saving" />
      </b-form-group>
      <b-form-group class="form-group_center" label="Allow create/update users on login" label-cols="4">
        <b-form-checkbox type="checkbox" v-model="settings.saml_create_update_users_on_login" :disabled="saving" />
      </b-form-group>
      <b-form-group label="Login Button Label" label-for="button-label" label-cols="4" :state="!serverErrors.saml_login_button_label">
        <b-form-input id="button-label" v-model="settings.saml_login_button_label" placeholder="e.g. Login with Okta" />
        <template #invalid-feedback>
          <div v-if="serverErrors.saml_login_button_label">
            {{ getServerFieldErrors('saml_login_button_label') }}
          </div>
        </template>
      </b-form-group>

      <h5 class="mt-5 mb-4">Service Provider (SP) Metadata</h5>
      <b-form-group label="Entity ID" label-cols="4">
        <b-input-group>
          <b-input type="text" :value="entityId" disabled />
          <b-input-group-append>
            <b-btn variant="info" @click="copy(entityId)">
              <i class="fa fa-copy" />
            </b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
      <b-form-group label="ACS URL" label-cols="4">
        <b-input-group>
          <b-input type="text" :value="acsUrl" disabled />
          <b-input-group-append>
            <b-btn variant="info" @click="copy(acsUrl)">
              <i class="fa fa-copy" />
            </b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>

      <!-- from IdP -->
      <h5 class="mt-5 mb-4">Identity Provider (IdP) Settings</h5>
      <b-form-group label="Issuer*" label-cols="4" :state="!serverErrors.saml_idp_issuer">
        <b-input v-model="settings.saml_idp_issuer" :disabled="saving" />
        <template #invalid-feedback>
          <div v-if="serverErrors.saml_idp_issuer">
            {{ getServerFieldErrors('saml_idp_issuer') }}
          </div>
        </template>
      </b-form-group>
      <b-form-group label="Sign On URL*" label-cols="4" :state="!serverErrors.saml_idp_sso_url">
        <b-input v-model="settings.saml_idp_sso_url" :disabled="saving" />
        <template #invalid-feedback>
          <div v-if="serverErrors.saml_idp_sso_url">
            {{ getServerFieldErrors('saml_idp_sso_url') }}
          </div>
        </template>
      </b-form-group>
      <b-form-group label="Sign Out URL*" label-cols="4" :state="!serverErrors.saml_idp_slo_url">
        <b-input v-model="settings.saml_idp_slo_url" :disabled="saving" />
        <template #invalid-feedback>
          <div v-if="serverErrors.saml_idp_slo_url">
            {{ getServerFieldErrors('saml_idp_slo_url') }}
          </div>
        </template>
      </b-form-group>
      <b-form-group label="X.509 Certificate*" label-cols="4" :state="!serverErrors.saml_idp_x509_cert">
        <b-form-textarea v-model="settings.saml_idp_x509_cert" :disabled="saving" rows="4" />
        <template #invalid-feedback>
          <div v-if="serverErrors.saml_idp_x509_cert">
            {{ getServerFieldErrors('saml_idp_x509_cert') }}
          </div>
        </template>
      </b-form-group>
    </template>
    <b-button variant="primary" v-if="hasChanges" :disabled="saving" @click="saveSettings">Save SAML Settings</b-button>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'

export default {
  name: 'Saml',
  data: () => ({
    saving: false,
    originSamlSettings: null,
    serverErrors: {}
  }),
  computed: {
    ...mapState('settings', ['samlSettings']),
    acsUrl: () => {
      return location.origin + '/api/v1/saml/acs'
    },
    entityId: () => {
      return location.origin + '/saml'
    },
    settings: {
      get() {
        return this.samlSettings
      },
      set(settings) {
        this.setSamlSettings(settings)
      }
    },
    hasChanges() {
      return JSON.stringify(this.settings) !== JSON.stringify(this.originSamlSettings)
    }
  },
  methods: {
    ...mapMutations('settings', ['setSamlSettings']),
    ...mapActions('settings', ['saveSamlSettings']),
    copy(textToCopy) {
      if (this.$clipboard(textToCopy)) {
        this.$appToast.success('Copied successfully')
      } else {
        this.$appToast.error('Coping failed')
      }
    },
    /**
     * @param fieldName
     * @returns {string} merged error messages splited by br tag
     */
    getServerFieldErrors(fieldName) {
      if (this.serverErrors?.[fieldName]) {
        return this.serverErrors[fieldName].join('<br>')
      }
      return ''
    },
    async saveSettings() {
      this.serverErrors = {}
      try {
        this.saving = true
        await this.saveSamlSettings(this.samlSettings)
        this.originSamlSettings = { ...this.samlSettings }
        this.$appToast.success('SAML Settings saved successfully!')
        return true
      } catch (error) {
        if (error.response?.status === 422) {
          this.$set(this, 'serverErrors', error.response.data.errors)
        } else {
          this.$appToast.error('Failed to Save SAML Settings!')
        }
        return false
      } finally {
        this.saving = false
      }
    },
    async loadSamlSettings() {
      await this.$store.dispatch('settings/loadSamlSettings')
      this.originSamlSettings = { ...this.samlSettings }
    }
  },
  mounted() {
    this.loadSamlSettings()
  }
}
</script>

<style lang="scss">
.saml-form {
  max-width: 800px;
}
</style>
